'use client';
import { useCallback, useSyncExternalStore } from 'react';
/**
 * Тема: светлая и тёмная, переключатель в шапке.
 *
 * Правила (решение владельца 2026-09-17, docs/DECISIONS.md):
 * - выбор сохраняется между визитами в localStorage под THEME_STORAGE_KEY;
 * - при первом заходе уважается системная настройка prefers-color-scheme;
 * - переключение без вспышки: THEME_BOOT_SCRIPT выставляет `data-theme` на
 *   <html> ещё в <head>, до первой отрисовки. Оба приложения обязаны вставлять
 *   его инлайн; тест dashboard сверяет index.html с этой константой.
 *
 * Пока явного выбора нет, тема следует за системной и меняется вместе с ней.
 * После явного выбора системная игнорируется - до сброса выбора.
 */
import { THEME_STORAGE_KEY } from './theme-boot';
export { THEME_BOOT_SCRIPT, THEME_STORAGE_KEY } from './theme-boot';
function isTheme(value) {
    return value === 'light' || value === 'dark';
}
/** Явный выбор пользователя или null, если его не было. */
export function readStoredTheme() {
    try {
        const value = window.localStorage.getItem(THEME_STORAGE_KEY);
        return isTheme(value) ? value : null;
    }
    catch {
        return null;
    }
}
export function systemTheme() {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
/** Тема, действующая сейчас: атрибут на <html>, иначе системная. */
export function currentTheme() {
    const attr = document.documentElement.getAttribute('data-theme');
    return isTheme(attr) ? attr : systemTheme();
}
export function applyTheme(theme, persist) {
    document.documentElement.setAttribute('data-theme', theme);
    /* Класс `dark` - для компонентов пресета, атрибут `data-theme` - для
       нашей разметки и сценариев. Оба выставляются здесь, чтобы признаки не
       могли разойтись (`theme-boot.ts`, тот же приём в инлайн-скрипте). */
    document.documentElement.classList.toggle('dark', theme === 'dark');
    if (!persist) {
        return;
    }
    try {
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
    catch {
        // Хранилище недоступно (приватный режим, запрет) - тема живёт до перезагрузки.
    }
}
/**
 * ПОДПИСЧИКИ НА ТЕМУ. Тема живёт не в React, а на `<html>`: её ставит
 * инлайн-скрипт ещё до первой отрисовки, меняет `applyTheme`, а на
 * лендинге - ещё и системная настройка. Значит источник у неё внешний, и
 * читать его надо `useSyncExternalStore`, а не состоянием компонента.
 */
const listeners = new Set();
function notify() {
    for (const listener of listeners)
        listener();
}
function subscribe(listener) {
    listeners.add(listener);
    /* Без явного выбора тема следует за системной и меняется вместе с ней. */
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
        if (readStoredTheme() !== null)
            return;
        applyTheme(media.matches ? 'dark' : 'light', false);
        notify();
    };
    media.addEventListener('change', onChange);
    return () => {
        listeners.delete(listener);
        media.removeEventListener('change', onChange);
    };
}
export function useTheme() {
    /*
     * СЕРВЕРНЫЙ СНИМОК - «светлая», И ЭТО НЕ УПРОЩЕНИЕ, А УСЛОВИЕ ГИДРАТАЦИИ.
     *
     * Прежняя редакция читала настоящую тему прямо в начальном состоянии
     * (`useState(() => currentTheme())`). На сервере темы нет, на клиенте
     * есть - первая клиентская отрисовка расходилась с серверной разметкой,
     * и React перерисовывал дерево целиком, снимая с `<html>` признаки
     * темы, поставленные инлайн-скриптом. На лендинге это значило: тёмная
     * тема выбрана, а страница светлая.
     *
     * Долгое время расхождение сводилось к разным `title` и `class` у одной
     * кнопки - такое React молча исправляет. Единица 15 поставила внутрь
     * кнопки настоящий `svg` библиотеки пресета, у солнца и луны разное
     * число контуров, и расхождение стало СТРУКТУРНЫМ - React перестал его
     * прощать. Поймано воротами доступности (`pnpm a11y`, лендинг в тёмной).
     *
     * `useSyncExternalStore` разводит два снимка: серверный отдаётся
     * гидратации, клиентский - сразу после неё. Разметка совпадает, тема
     * применяется, признаки на `<html>` остаются.
     */
    const theme = useSyncExternalStore(subscribe, () => currentTheme(), () => 'light');
    const setTheme = useCallback((next) => {
        applyTheme(next, true);
        notify();
    }, []);
    const toggle = useCallback(() => {
        setTheme(currentTheme() === 'dark' ? 'light' : 'dark');
    }, [setTheme]);
    return { theme, setTheme, toggle };
}
//# sourceMappingURL=theme.js.map